// One workload (02-DESIGN/04-workloads/01-the-workload-model.md § the verbs;
// 04-placement-records.md § who reads, § the console surface; 01-the-console.md
// § the words; § conventions: density and length): the name, its state and
// *Stop* or *Start* first, then five facts — its solution and, for a model,
// the model, each a link to its page (§ conventions: a name is a link) — what
// it answers as and reaches as one counted line, and its configuration
// counted, each opening its list in place under the line, the id quiet and
// last with copy; *Change* a form in the Overview tab's place (§ conventions: a form);
// *Stop* and *Start* for an admin; *Remove*, offered twice; its count, its generation and the last good one,
// and a row per copy — placement, executor, status, failures, generation
// (04-placement-records.md § the console surface; decision 0035); its settings,
// to its registrant and an admin (01-the-configuration-service.md § the console:
// a workload's own page shows its documents to its registrant the same way,
// through the same verbs); its output live — an admin's on the records family,
// a member's through the records verb — and recent, its resources over time,
// its events.

import { identity } from "@impire-io/sdk";
import { useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { usePage } from "../../platform/session.tsx";
import { pageOffered } from "../../roles/offers.ts";
import {
  Button,
  Field,
  NotAllowed,
  PageHead,
  Pending,
  Pill,
  plural,
  since,
  TextInput,
  useLoad,
  when,
} from "../../ui/components.tsx";
import {
  idSubject,
  PLATFORM_REGISTRANT,
  RegistrationState,
  SERVICE,
  validWorkloadID,
  type SlotState,
  type StateReply,
  type UpdateRequest,
  type Workload,
} from "../../wire/workloads.ts";
import { RowMenu, Table, type MenuEntry } from "../../ui/table.tsx";
import { Tabs, type TabSpec } from "../../ui/tabs.tsx";
import { documentAddress, sorted } from "../configuration/documents.ts";
import { useDocuments, WorkloadSettings } from "../configuration/WorkloadSettings.tsx";
import { DOCUMENT_SETTINGS, documentOf, type Entry } from "../../wire/config.ts";
import { useInstalled } from "../solutions/installed.ts";
import { digestOf, provenance } from "../solutions/words.ts";
import { formOf, FormPage, useFormState } from "../../ui/form.tsx";
import { Ident, NameLink } from "../../ui/ident.tsx";
import { PlanRefused, usePlan } from "../settings/PlanNotes.tsx";
import { useSurface } from "../surface.tsx";
import { useManaged } from "../workspaces/managed.ts";
import {
  configurationOf,
  copiesOf,
  COPIES_HINT,
  egressOf,
  namesOf,
  PairsEditor,
  RulesEditor,
  type Pair,
} from "./editors.tsx";
import { REMOVE_CONSEQUENCE, START_CONSEQUENCE, stopConsequence } from "./acts.ts";
import { EventsPane, OutputPane, SamplesPane } from "./panes.tsx";
import {
  copies,
  copyName,
  generationLine,
  plainState,
  plainStatus,
  reachLine,
  runningCount,
  slotsOf,
  whyNot,
} from "./state.ts";

/** WorkloadTab is one of a workload's tabs (01-the-console.md § conventions: the pages that have tabs). */
export type WorkloadTab = "overview" | "output" | "samples" | "events" | "settings";

/** WorkloadForm is a form a workload's page shows in a tab's place. */
export type WorkloadForm = "change" | "add";

/** formTab is the tab a workload's form belongs to: *Change* Overview's, *Add settings* Settings'. */
export function formTab(open: string | undefined): WorkloadTab | undefined {
  return open === "change" ? "overview" : open === "add" ? "settings" : undefined;
}

/** initialTab reads the tab a link asked a workload's page to open on: Settings, or nothing. */
export function initialTab(state: unknown): WorkloadTab | undefined {
  return typeof state === "object" && state !== null && (state as { tab?: unknown }).tab === "settings"
    ? "settings"
    : undefined;
}

export function WorkloadPage() {
  const { id = "" } = useParams();
  const s = useSurface();
  if (!validWorkloadID(id)) {
    return (
      <>
        <PageHead
          crumb={<Link to={tenantPath(s.tenant, "workloads")}>Workloads</Link>}
          title="Not a workload"
          line={`"${id}" is not a workload's id.`}
        />
      </>
    );
  }
  return <One id={id} />;
}

function One({ id }: { id: string }) {
  const s = useSurface();
  const managed = useManaged();
  const [load, reload] = useLoad(id, () => s.bus.call<Workload>(SERVICE, idSubject("get", id), undefined));
  const [err, setErr] = useState<unknown>(undefined);
  const { platform } = usePage();
  const location = useLocation();
  const navigate = useNavigate();
  // *Change* is the Overview tab's form, *Add settings* the Settings tab's (WorkloadSettings).
  const form = useFormState<WorkloadForm>();
  // Which counted line has its list open under it, in place (§ conventions: density and length).
  const [opened, setOpened] = useState<"reach" | "configuration" | undefined>(undefined);
  // A document's page returning here asks for the Settings tab in its link's state.
  const [chosen, setTab] = useState<WorkloadTab>(
    () => initialTab(location.state) ?? formTab(formOf(location.state)) ?? "overview",
  );
  // A form open in the history entry — after a refresh, or Back and Forward — shows its tab,
  // and the tab stays chosen when the form closes.
  const wanted = formTab(form.open);
  if (wanted !== undefined && wanted !== chosen) {
    setTab(wanted);
  }
  const tab = wanted ?? chosen;
  const installed = useInstalled(s.bus);
  // The crumb names the workload by its name, never its id (§ conventions: density and length).
  const crumb = (name?: string) => (
    <>
      <Link to={tenantPath(s.tenant, "workloads")}>Workloads</Link>
      {name !== undefined && (
        <>
          <span>/</span>
          <span>{name}</span>
        </>
      )}
    </>
  );

  const act = (verb: "retire" | "suspend" | "resume", timeout = 30000) => {
    setErr(undefined);
    s.bus.call<StateReply>(SERVICE, idSubject(verb, id), undefined, timeout).then(
      () => {
        reload();
      },
      (e: unknown) => {
        setErr(e);
      },
    );
  };

  if (load.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb()}</div>
        <Pending what="Reading the workload" />
      </>
    );
  }
  if (load.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb()} title="This workload" />
        <NotAllowed err={load.err} what="This workload" />
      </>
    );
  }
  const w = load.value;
  const part = w.solution !== undefined ? provenance(w.solution, installed) : undefined;
  // A platform workload — the workspace's vault, its state service — is the
  // platform's: nobody in the workspace changes or removes it, and an admin
  // may still stop and start it (02-the-fleet.md § the platform's own workloads).
  const platforms = w.registrant === PLATFORM_REGISTRANT;
  // A required workload is the blueprint's: nobody here changes or removes it,
  // an admin may stop and start it, and the steward starts it again at its
  // next pass (02-DESIGN/13-blueprints/02-convergence.md § the two refusals).
  // The steward itself is not stopped from the workspace.
  const required = w.required === true;
  const steward = platforms && w.platform === "steward";
  // A solution's workload is the solution's: changed by an update of it and
  // removed with it, never on its own (01-the-manifest.md § install).
  const mine = s.manages(w.registrant) && !platforms && !required && w.solution === undefined;
  const stoppable =
    (platforms || required || part !== undefined) && !steward && s.offered("workloads:suspend");
  const p = plainState(w);
  const active = w.state === RegistrationState.active;
  const gone = w.state === RegistrationState.retired;
  const failures = w.placement.failures ?? 0;
  const settingsFor =
    s.offered("configuration:any") || (s.offered("configuration:own") && w.registrant === s.me);
  const digest = digestOf(w.digest) ?? w.digest;
  const reach = reachLine(w);
  const count = runningCount(w);
  // A model's workload names its model, a link to the model's page where the role is offered Models.
  const inference = platform.images?.[identity.Image.inference] || undefined;
  const isModel =
    inference !== undefined &&
    (w.image === inference || w.digest === inference) &&
    pageOffered(s.role, "models");
  // *Change* shows only for a workload this person may change while it runs.
  const changing = form.open === "change" && mine && active;
  const toggle = (line: "reach" | "configuration") => {
    setOpened(opened === line ? undefined : line);
  };
  const many = copies(w) > 1;
  // Why it cannot start, as the workload service names it (decision 0034).
  const why = active ? whyNot(w.placement) : "";
  // Stop or Start is the overview's one action, beside the state (01-the-console.md
  // § conventions: density and length — the first screen says the state and the one action).
  const stopStart = (
    <>
      {required && active && (
        <span className="small muted">
          {managed.by || "The blueprint"}&apos;s steward will start it again
        </span>
      )}
      {s.offered("workloads:suspend") && active && !steward && (
        <PrimaryConfirm
          label="Stop"
          consequence={stopConsequence(w, managed.by || "the blueprint")}
          onConfirm={() => {
            act("suspend");
          }}
        />
      )}
      {s.offered("workloads:suspend") && w.state === RegistrationState.suspended && (
        <PrimaryConfirm
          label="Start"
          consequence={START_CONSEQUENCE}
          onConfirm={() => {
            act("resume");
          }}
        />
      )}
    </>
  );
  // The page's menu: *Remove* last, asked first (§ conventions: remove); a
  // solution's workload is removed with its solution, on the solution's page.
  const menu: MenuEntry[] = gone
    ? []
    : [
        ...(part !== undefined && s.offered("solutions:manage")
          ? [
              {
                label: "Open the solution to remove it",
                onSelect: () => {
                  void navigate(tenantPath(s.tenant, "solutions", part.name));
                },
              },
            ]
          : []),
        ...(mine
          ? [
              {
                label: "Remove",
                danger: true as const,
                confirm: { title: `Remove ${w.name}?`, consequence: REMOVE_CONSEQUENCE },
                onSelect: () => {
                  act("retire");
                },
              },
            ]
          : []),
      ];
  const tabs: TabSpec<WorkloadTab>[] = [
    { id: "overview", label: "Overview" },
    { id: "output", label: "Output" },
    { id: "samples", label: "Samples" },
    { id: "events", label: "Events" },
    ...(settingsFor ? [{ id: "settings" as const, label: "Settings" }] : []),
  ];
  return (
    <>
      <PageHead
        crumb={crumb(w.name)}
        title={w.name}
        line={
          part !== undefined
            ? part.line
            : w.description !== undefined && w.description !== ""
              ? w.description
              : `Added by ${w.display ?? w.registrant}, ${since(w.created) || when(w.created)}.`
        }
        action={
          tab === "overview" && !changing && (mine || stoppable) && !gone ? (
            <>
              {mine && active && (
                <Button
                  onClick={() => {
                    form.show("change");
                  }}
                >
                  Change
                </Button>
              )}
              {stopStart}
              {menu.length > 0 && <RowMenu label={`Actions for ${w.name}`} entries={menu} />}
            </>
          ) : undefined
        }
      />
      {err !== undefined && <NotAllowed err={err} />}

      <Tabs label={w.name} tabs={tabs} value={tab} onChange={setTab}>
        {tab === "overview" && changing && (
          <Change
            w={w}
            onClose={form.close}
            onDone={() => {
              form.close();
              reload();
            }}
          />
        )}
        {tab === "overview" && !changing && (
          <section className="card">
            <div className="row" style={{ marginBottom: 20 }}>
              <Pill tone={p.tone}>{p.word}</Pill>
              {required && <Pill strong>required by {managed.by || "the blueprint"}</Pill>}
              <span className="small muted">
                since {when(w.state_changed)}
                {w.state_by !== "" && w.state_by !== w.registrant && <> · by {w.state_by}</>}
                {why !== "" && <> · {why}</>}
                {count !== "" && <> · {count}</>}
                {!many && w.placement.executor !== "" && <> · on {w.placement.executor}</>}
                {!many && failures > 0 && <> · {plural(failures, "failure")}</>}
              </span>
            </div>
            <dl className="kv">
              <dt>Runs</dt>
              <dd>
                <span className="mono">{w.image}</span>
                {w.architectures.length > 0 && (
                  <span className="small muted"> · {w.architectures.join(", ")}</span>
                )}
                {digest !== "" && (
                  <div className="small">
                    <Ident value={digest} label="digest" />
                  </div>
                )}
              </dd>
              <dt>Resources</dt>
              <dd>
                {w.resources.cpus} vCPU · {w.resources.memory_mib} MiB
              </dd>
              <dt>Copies</dt>
              <dd>
                {copies(w)}
                {generationLine(w) !== "" && <span className="small muted"> · {generationLine(w)}</span>}
              </dd>
              <dt>Added by</dt>
              <dd>
                {platforms ? "Impire, for the workspace" : (w.display ?? w.registrant)}
                <span className="muted">
                  {" "}
                  · {when(w.created)} · changed {when(w.updated)}
                </span>
              </dd>
              {part !== undefined && (
                <>
                  <dt>Solution</dt>
                  <dd>
                    {pageOffered(s.role, "solutions") ? (
                      <NameLink to={tenantPath(s.tenant, "solutions", part.name)}>{part.display}</NameLink>
                    ) : (
                      part.display
                    )}
                  </dd>
                </>
              )}
              {isModel && (
                <>
                  <dt>Model</dt>
                  <dd>
                    <NameLink to={tenantPath(s.tenant, "models", w.id)}>{w.name}</NameLink>
                    <span className="small muted"> · under Models</span>
                  </dd>
                </>
              )}
              <dt>Configuration</dt>
              <dd>
                {settingsFor ? (
                  <WithSettings workload={w.id}>
                    {(docs) => (
                      <ConfigurationLine
                        w={w}
                        docs={docs}
                        open={opened === "configuration"}
                        onToggle={() => {
                          toggle("configuration");
                        }}
                        toSettings={() => {
                          setTab("settings");
                        }}
                      />
                    )}
                  </WithSettings>
                ) : (
                  <ConfigurationLine
                    w={w}
                    docs={[]}
                    open={opened === "configuration"}
                    onToggle={() => {
                      toggle("configuration");
                    }}
                  />
                )}
              </dd>
              <dt>Reach</dt>
              <dd>
                {reach.count === 0 ? (
                  <span className="faint">{reach.line}</span>
                ) : (
                  <button
                    type="button"
                    className="text-link"
                    aria-expanded={opened === "reach"}
                    onClick={() => {
                      toggle("reach");
                    }}
                  >
                    {reach.line}
                  </button>
                )}
                {opened === "reach" && <ReachLists w={w} />}
              </dd>
              <dt>Id</dt>
              <dd>
                <Ident value={w.id} label="id" />
              </dd>
            </dl>
          </section>
        )}
        {tab === "overview" && !changing && !gone && <Copies w={w} />}
        {tab === "output" && <OutputPane id={id} placement={w.placement} />}
        {tab === "samples" && <SamplesPane id={id} placement={w.placement} />}
        {tab === "events" && <EventsPane id={id} placement={w.placement} />}
        {tab === "settings" && settingsFor && (
          <WorkloadSettings
            workload={id}
            image={w.image}
            canEdit={!gone}
            compact
            back={{ to: tenantPath(s.tenant, "workloads", id), label: w.name, tab: "settings" }}
          />
        )}
      </Tabs>
    </>
  );
}

/**
 * configurationLine is a workload's configuration counted (01-the-console.md
 * § the moves, page by page: a workload's page): its settings by name — the
 * console's word for a settings document, whose plural is the same word, so
 * they are named rather than counted while few — then the plain values it
 * starts with; *none* where it has neither.
 */
export function configurationLine(names: readonly string[], values: number): string {
  const parts: string[] = [];
  if (names.length > 0) {
    const shown = names.slice(0, 2).join(", ");
    parts.push(names.length > 2 ? `${shown} and ${String(names.length - 2)} more` : shown);
  }
  if (values > 0) {
    parts.push(`starts with ${String(values)} ${values === 1 ? "value" : "values"}`);
  }
  return parts.length === 0 ? "none" : parts.join(" · ");
}

/** WithSettings follows a workload's settings through the leased watch and hands their entries on: read only where the role may read them. */
function WithSettings({ workload, children }: { workload: string; children: (docs: Entry[]) => ReactNode }) {
  const { view } = useDocuments(workload);
  return <>{children(sorted(view.docs, DOCUMENT_SETTINGS))}</>;
}

/** ConfigurationLine is the counted line and, opened in place under it, each settings with its version and the plain values. */
function ConfigurationLine({
  w,
  docs,
  open,
  onToggle,
  toSettings,
}: {
  w: Workload;
  docs: Entry[];
  open: boolean;
  onToggle: () => void;
  toSettings?: () => void;
}) {
  const s = useSurface();
  const values = Object.entries(w.configuration ?? {});
  const line = configurationLine(
    docs.map((e) => documentOf(e.key)),
    values.length,
  );
  if (line === "none") {
    return <span className="faint">none</span>;
  }
  // A settings' page returns to this workload's Settings tab (DocumentPage.tsx).
  const back = { back: { to: tenantPath(s.tenant, "workloads", w.id), label: w.name, tab: "settings" } };
  return (
    <>
      <button type="button" className="text-link" aria-expanded={open} onClick={onToggle}>
        {line}
      </button>
      {open && (
        <div className="small">
          {docs.map((e) => (
            <div key={e.key}>
              <Link
                className="text-link name-link mono"
                to={documentAddress(s.tenant, w.id, documentOf(e.key))}
                state={back}
              >
                {documentOf(e.key)}
              </Link>
              <span className="muted"> · version {e.revision}</span>
            </div>
          ))}
          {values.map(([k, v]) => (
            <div className="mono" key={k}>
              {k}={v}
            </div>
          ))}
          {toSettings !== undefined && (
            <button type="button" className="text-link" onClick={toSettings}>
              Open the Settings tab
            </button>
          )}
        </div>
      )}
    </>
  );
}

/**
 * PrimaryConfirm is *Stop* or *Start*, the Overview's primary action, asking
 * first in place with what it does — a stop is undone by a start, but what a
 * stop interrupts is not.
 */
function PrimaryConfirm({
  label,
  consequence,
  onConfirm,
}: {
  label: string;
  consequence: string;
  onConfirm: () => void;
}) {
  const [armed, setArmed] = useState(false);
  if (!armed) {
    return (
      <Button
        kind="primary"
        onClick={() => {
          setArmed(true);
        }}
      >
        {label}
      </Button>
    );
  }
  return (
    <span className="confirm" role="group">
      <span className="why">{consequence}</span>
      <Button
        kind="primary"
        size="sm"
        onClick={() => {
          setArmed(false);
          onConfirm();
        }}
      >
        {label}
      </Button>
      <Button
        kind="quiet"
        size="sm"
        onClick={() => {
          setArmed(false);
        }}
      >
        Keep
      </Button>
    </span>
  );
}

/** ReachLists are what a workload answers as and may reach, opened in place under their counted line. */
function ReachLists({ w }: { w: Workload }) {
  return (
    <dl className="kv small">
      <dt>Answers as</dt>
      <dd>
        {(w.names ?? []).length === 0 ? (
          <span className="faint">no name: no model or toolkit is found here</span>
        ) : (
          (w.names ?? []).map((n) => (
            <div className="mono" key={n}>
              {n}
            </div>
          ))
        )}
      </dd>
      <dt>May reach</dt>
      <dd>
        {(w.egress ?? []).length === 0 ? (
          <span className="faint">nothing outside Impire</span>
        ) : (
          (w.egress ?? []).map((r) => (
            <div className="mono" key={r}>
              {r}
            </div>
          ))
        )}
      </dd>
    </dl>
  );
}

/** Copies is a row per slot: placement, executor, status, failures, generation (04-placement-records.md § the console surface). */
function Copies({ w }: { w: Workload }) {
  const slots = slotsOf(w);
  return (
    <section className="section" style={{ marginTop: 24 }}>
      <div className="section-head">
        <h2>Copies</h2>
      </div>
      <Table<SlotState>
        label="Copies"
        rows={slots}
        rowKey={(x) => String(x.slot)}
        sort={{ key: "copy", dir: "asc" }}
        columns={[
          {
            key: "copy",
            header: "Copy",
            width: "90px",
            value: (x) => x.slot,
            text: (x) => copyName(x.slot),
            cell: (x) => <span>{copyName(x.slot)}</span>,
          },
          {
            key: "status",
            header: "Status",
            width: "minmax(130px, 1.4fr)",
            facet: "Status",
            value: (x) => plainStatus(x.status).word,
            text: (x) => `${plainStatus(x.status).word} ${whyNot(x)}`,
            cell: (x) => {
              const p = plainStatus(x.status);
              const why = whyNot(x);
              return (
                <span className="cell">
                  <span>
                    <Pill tone={p.tone}>{p.word}</Pill>
                  </span>
                  {why !== "" && <span className="cell-sub">{why}</span>}
                </span>
              );
            },
          },
          {
            key: "executor",
            header: "Host",
            value: (x) => x.executor ?? "",
            cell: (x) =>
              x.executor !== undefined && x.executor !== "" ? (
                <span className="mono small">{x.executor}</span>
              ) : (
                <span className="faint">none yet</span>
              ),
          },
          {
            key: "failures",
            header: "Failures",
            width: "90px",
            narrow: false,
            value: (x) => x.failures ?? 0,
            cell: (x) => <span className="small muted">{x.failures ?? 0}</span>,
          },
          {
            key: "generation",
            header: "Generation",
            width: "100px",
            narrow: false,
            value: (x) => x.generation ?? 0,
            cell: (x) => <span className="small muted">{x.generation ?? "—"}</span>,
          },
          {
            key: "placement",
            header: "Id",
            narrow: false,
            value: (x) => x.placement ?? "",
            cell: (x) => <span className="mono small muted">{x.placement ?? ""}</span>,
          },
        ]}
      />
    </section>
  );
}

function Change({ w, onClose, onDone }: { w: Workload; onClose: () => void; onDone: () => void }) {
  // A change of resources or copies the plan refuses names the plan (02-DESIGN/17-plans/02-features-and-limits.md § the console).
  const plan = usePlan();
  const s = useSurface();
  const [image, setImage] = useState(w.image);
  const [cpus, setCpus] = useState(String(w.resources.cpus));
  const [memory, setMemory] = useState(String(w.resources.memory_mib));
  const [pairs, setPairs] = useState<Pair[]>(
    Object.entries(w.configuration ?? {}).map(([key, value]) => ({ key, value })),
  );
  const [rules, setRules] = useState<string[]>(w.egress ?? []);
  const [names, setNames] = useState((w.names ?? []).join(", "));
  const [count, setCount] = useState(String(copies(w)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const submit = () => {
    if (busy) {
      return;
    }
    setErr(undefined);
    const configuration = configurationOf(pairs);
    if (configuration instanceof Error) {
      setErr(configuration);
      return;
    }
    const egress = egressOf(rules);
    if (egress instanceof Error) {
      setErr(egress);
      return;
    }
    const declared = namesOf(names);
    if (declared instanceof Error) {
      setErr(declared);
      return;
    }
    const replicas = copiesOf(count);
    if (replicas instanceof Error) {
      setErr(replicas);
      return;
    }
    const body: UpdateRequest = {};
    if (replicas !== copies(w)) {
      body.replicas = replicas;
    }
    if (image.trim() !== w.image) {
      body.image = image.trim();
    }
    if (Number(cpus) !== w.resources.cpus || Number(memory) !== w.resources.memory_mib) {
      body.resources = { cpus: Number(cpus), memory_mib: Number(memory) };
    }
    body.configuration = configuration;
    body.egress = egress;
    if (declared.join(",") !== (w.names ?? []).join(",")) {
      body.names = declared;
    }
    setBusy(true);
    s.bus.call<StateReply>(SERVICE, idSubject("update", w.id), body, 60000).then(
      () => {
        setBusy(false);
        onDone();
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  return (
    <FormPage
      title={`Change ${w.name}`}
      heading="h2"
      back="Overview"
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <p className="muted">
        {copies(w) > 1
          ? "The running copies are replaced by changed ones, one at a time; if the change cannot start, the last that ran is put back."
          : "The running copy is replaced by the changed one; if it cannot start, the last that ran is put back."}
      </p>
      {err !== undefined && <PlanRefused err={err} what="Change" holder={plan.holder} />}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="Image">
          <TextInput value={image} onChange={setImage} mono required />
        </Field>
        <div className="two">
          <Field label="Virtual CPUs">
            <TextInput value={cpus} onChange={setCpus} type="number" />
          </Field>
          <Field label="Memory (MiB)">
            <TextInput value={memory} onChange={setMemory} type="number" />
          </Field>
        </div>
        <Field label="Copies" hint={COPIES_HINT}>
          <TextInput value={count} onChange={setCount} type="number" />
        </Field>
        <Field
          label="Answers as"
          hint="The model or toolkit names it is found by, separated by commas. Each is this workload's alone in the workspace."
        >
          <TextInput value={names} onChange={setNames} placeholder="fast, gpt-5" mono />
        </Field>
        <div className="field">
          <span className="label">Configuration</span>
          <PairsEditor pairs={pairs} onChange={setPairs} />
        </div>
        <div className="field">
          <span className="label">May reach</span>
          <RulesEditor rules={rules} onChange={setRules} />
        </div>
      </form>
    </FormPage>
  );
}
